import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import DashboardHeader from "../components/DashboardHeader";

const BASE_URL = process.env.REACT_APP_API_BASE_URL

const QuizDetailPage = () => {
    const { id } = useParams();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [quizDetail, setQuizDetail] = useState(null);
    const [userData, setUserData] = useState({
        name: "Loading...",
        initials: "--",
        notificationCount: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profilePhoto, setProfilePhoto] = useState(null);

    const accessToken = useSelector((state) => state.auth.access)
    const user = useSelector((state) => state.auth.user);

    // Fetch profile photo separately
      useEffect(() => {
        const fetchUserProfile = async () => {
          try {
            const token = localStorage.getItem("access");
            const res = await axios.get(`${BASE_URL}/users/profile/`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const p = res.data.profile_photo;
            if (p) {
              setProfilePhoto(
                p.startsWith("http") ? p : `${BASE_URL}${p}`
              );
            } else {
              setProfilePhoto(null);
            }
          } catch (e) {
            setProfilePhoto(null);
          }
        };
        fetchUserProfile();
      }, []);

    const fetchUserData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/users/profile/`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
            });
            setUserData({
                name: `${response.data.first_name} ${response.data.last_name}`,
                initials: `${response.data.first_name[0]}${response.data.last_name[0]}`,
                notificationCount: response.data.notification_count || 0,
            });
        } catch (err) {
            //console.error("Failed to fetch user data:", err.response?.data || err.message);
        }
    };

    const fetchQuizDetail = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/quiz/quiz-attempts/${id}/`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
            });
            const attempt = response.data;
            const quiz = attempt.quiz || {};

            const lesson = quiz.lesson || {};
            const module = lesson.module || {};
            const course = module.course || { title: "Unknown Course" };

            const totalQuestions = quiz.questions?.length || 0;
            const correctAnswers = totalQuestions > 0 ? Math.round((attempt.score / 100) * totalQuestions) : 0;
            const incorrectAnswers = totalQuestions - correctAnswers;

            // Handle user_answers as an array of objects or fallback to empty array
            const userAnswersArray = Array.isArray(attempt.user_answers) ? attempt.user_answers : [];
            const userAnswersMap = new Map(
                userAnswersArray.map((ans) => [parseInt(ans.question_id), parseInt(ans.selected_option_id)])
            );

            const questions = quiz.questions?.map((q) => {
                const options = Array.isArray(q.options) ? q.options : [];
                const correctOption = options.find((opt) => opt.is_correct) || null;
                const userSelectedOptionId = userAnswersMap.get(q.id);
                const givenAnswer = options.find((opt) => opt.id === userSelectedOptionId)?.text || "Not answered";
                const correctAnswer = correctOption ? correctOption.text : "N/A";
                const result = userSelectedOptionId && correctOption && userSelectedOptionId === correctOption.id ? "Correct" : "Incorrect";

                return {
                    id: q.id,
                    type: "Multiple Choice",
                    question: q.text || "No question text",
                    givenAnswer,
                    correctAnswer,
                    result,
                };
            }) || [];

            setQuizDetail({
                id: attempt.id,
                courseName: course.title || "Unknown Course",
                quizName: quiz.title || (lesson.title ? `${lesson.title} Quiz` : "Untitled Quiz"),
                quizTime: "0 Minutes",
                attemptTime: "N/A",
                attempt: {
                    date: new Date(attempt.completed_at).toLocaleString(),
                    question: totalQuestions,
                    totalMarks: totalQuestions,
                    passMarks: (quiz.passing_percentage / 100) * totalQuestions || 0,
                    passPercentage: quiz.passing_percentage || 0,
                    correctAnswers,
                    incorrectAnswers,
                    earnedMarks: correctAnswers,
                    earnedPercentage: attempt.score,
                    result: attempt.score >= (quiz.passing_percentage || 0) ? "Pass" : "Fail",
                },
                questions,
            });
        } catch (err) {
            setError("Failed to fetch quiz details.");
            //console.error("Quiz detail fetch error:", err.response?.data || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserData();
        fetchQuizDetail();
    }, [id]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;
    if (!quizDetail) return null;

    return (
        <div className="dashboard-container">
            <div className="d-flex">
                <Sidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}/>
                <div className="main-content flex-grow-1">
                    <DashboardHeader user={user} profilePhoto={profilePhoto} onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
                    <div className="dashboard-content p-4">
                        <div className="mb-3">
                            <Link to="/quiz-attempts" className="btn instructor-name p-0 text-decoration-none">
                                <i className="fas fa-arrow-left me-2"></i>
                                Back
                            </Link>
                        </div>

                        <div className="course-info mb-3">
                            <span className="text-muted">Course: </span>
                            <span className="fw-medium">{quizDetail.courseName}</span>
                        </div>

                        <h2 className="dashboard-title mb-3">{quizDetail.quizName}</h2>

                        <div className="quiz-timing mb-4">
                            {/*<span className="me-4">
                <span className="text-muted">Quiz Time: </span>
                <span className="fw-medium">{quizDetail.quizTime}</span>
              </span>
              <span>
                <span className="text-muted">Attempt Time: </span>
                <span className="fw-medium">{quizDetail.attemptTime}</span>
              </span>*/}
                        </div>

                        <div className="quiz-results-table mb-5">
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Question</th>
                                            <th>Total Marks</th>
                                            <th>Pass Marks</th>
                                            <th>Correct Answer</th>
                                            <th>Incorrect Answer</th>
                                            <th>Earned Marks</th>
                                            <th>Result</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{quizDetail.attempt.date}</td>
                                            <td className="text-center">{quizDetail.attempt.question}</td>
                                            <td className="text-center">{quizDetail.attempt.totalMarks.toFixed(2)}</td>
                                            <td className="text-center">
                                                {quizDetail.attempt.passMarks.toFixed(2)}
                                                <br />
                                                <small className="text-muted">({quizDetail.attempt.passPercentage}%)</small>
                                            </td>
                                            <td className="text-center">{quizDetail.attempt.correctAnswers}</td>
                                            <td className="text-center">{quizDetail.attempt.incorrectAnswers}</td>
                                            <td className="text-center">
                                                {quizDetail.attempt.earnedMarks.toFixed(2)} ({quizDetail.attempt.earnedPercentage.toFixed(2)}%)
                                            </td>
                                            <td className="text-center">
                                                <span className={`badge ${quizDetail.attempt.result === "Fail" ? "bg-danger" : "bg-success"}`}>
                                                    {quizDetail.attempt.result}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="quiz-overview">
                            <h4 className="section-title mb-4">Quiz Overview</h4>

                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: "60px" }}>No</th>
                                            <th style={{ width: "100px" }}>Type</th>
                                            <th>Questions</th>
                                            <th>Given Answer</th>
                                            <th>Correct Answer</th>
                                            <th style={{ width: "100px" }}>Result</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quizDetail.questions.map((question, index) => (
                                            <tr key={question.id}>
                                                <td className="text-center">
                                                    <div className="question-number">
                                                        <i className="fas fa-check-circle text-success"></i>
                                                        <span className="ms-1">{index + 1}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <i className="fas fa-info-circle text-muted me-1"></i>
                                                    {question.type}
                                                </td>
                                                <td>{question.question}</td>
                                                <td>{question.givenAnswer}</td>
                                                <td>{question.correctAnswer}</td>
                                                <td className="text-center">
                                                    <span className={`badge ${question.result === "Incorrect" ? "bg-danger" : "bg-success"}`}>
                                                        {question.result}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuizDetailPage;